/* eslint-disable operator-linebreak */
/* eslint-disable function-paren-newline */
/* eslint-disable implicit-arrow-linebreak */
/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CommonDropdown,
  RegularButton,
  WorkInProgress,
  SearchInput,
  Loader,
  ChartsCard,
  IChartData,
} from 'pg-ui-library';

import useDebounce from 'src/hooks/useDebounce';
import { useTypedSelector } from 'src/hooks/useTypedSelector';
import { MessagesBulletinsTable } from 'src/components';
import { useQuery } from '@tanstack/react-query';
import EmployeesService from 'src/services/employees.service';
import { IEmployee } from 'src/interfaces/employee';
import { EmployeeListTable } from 'src/components/employee-list-table';
import ApprovalsService from 'src/services/approvals.service';
import { EmployeeAccessRequestTable } from './components/employee-access-request-table';

import { dropdownOptions1, dropdownOptions2 } from './mocks/employee-management.mock';
import {
  GridContainer,
  PageTitle,
  HeaderText,
  CardItemContainer,
  ProfileSmallCard,
  ProfileSmallCardTitle,
  ProfileSmallCardValue,
  CardFormsContainer,
  NavTextLink,
  MessagesBulletinsContainer,
  ProfileContainer,
  EmployeeContainer,
  CardHeaderWithButton,
  ActionsContainer,
} from './styles';
import ActiveInactiveEmployeesChart from './components/active-inactive-chart';
import EmployeesByDepartmentChart from './components/employees-by-department-chart';

const employeeListInitialColumns = [
  {
    name: 'Emp.ID',
    selector: (row: IEmployee) => row.employeeId,
    sortField: 'id',
    width: '120px',
    omit: false,
  },
  {
    name: 'SSN',
    selector: (row: IEmployee) => row.ssn,
    width: '180px',
    omit: false,
  },
  {
    name: 'Name',
    selector: (row: IEmployee) => row.name,
    width: '180px',
    sortField: 'firstName',
    omit: false,
    sortable: true,
    reorder: true,
  },
  {
    name: 'Birth Date',
    selector: (row: IEmployee) => row.birthDate,
    width: '180px',
    sortField: 'birthDate',
    omit: false,
    sortable: true,
    reorder: true,
  },
  {
    name: 'Work Location',
    selector: (row: IEmployee) => row.workLocation,
    width: '180px',
    omit: false,
    wrap: true,
  },
  {
    name: 'Department',
    selector: (row: IEmployee) => row.department,
    width: '180px',
    omit: false,
  },
  {
    name: 'Job',
    selector: (row: IEmployee) => row.job,
    width: '180px',
    omit: false,
  },
  {
    name: 'Position',
    selector: (row: IEmployee) => row.position,
    width: '180px',
    omit: false,
  },
  {
    name: 'Email',
    selector: (row: IEmployee) => row.email,
    width: '300px',
    sortField: 'primaryContactEmail',
    omit: false,
    sortable: true,
    reorder: true,
  },
];

const EmployeeMangement: React.FC = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  // Debounce the search term so that we only perform the search
  // after the user has stopped typing for 500ms
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const { config } = useTypedSelector(state => state.config.data);
  const [glanceChartData, setGlanceChartData] = useState<IChartData[]>();
  const [departmentChartData, setDepartmentChartData] = useState<IChartData[]>();

  const companyId = config?.selectedCompanyId;

  const {
    isLoading: isLoadingChartEmployeeGlance,
    isFetching: isFetchingChartEmployeeGlance,
    data: employeeGlance,
    refetch: getEmployeeGlanceByCompany,
  } = useQuery(['employee/glance'], () => EmployeesService.getEmployeeGlanceByCompany(companyId as unknown as string), {
    enabled: false,
  });

  const {
    isLoading: isLoadingChartByDepartment,
    isFetching: isFetchingChartByDepartment,
    data: employeesByDepartment,
    refetch: getEmployeesByDepartment,
  } = useQuery(
    ['employee/departments'],
    () => EmployeesService.getEmployeesByDepartment(companyId as unknown as string),
    { enabled: false }
  );

  const {
    isLoading: isLoadingPendingChanges,
    isFetching: isFetchingPendingChanges,
    data: pending,
  } = useQuery(['approvals/pendings/total'], () =>
    ApprovalsService.getPendingApprovalsTotal(companyId as unknown as string)
  );

  useEffect(() => {
    if (companyId) {
      getEmployeeGlanceByCompany();
      getEmployeesByDepartment();
    }
  }, [companyId]);

  useEffect(() => {
    setGlanceChartData([
      {
        name: `Active Employees: ${employeeGlance?.active ?? 0}`,
        value: employeeGlance?.active ?? 0,
        color: '#0000AA',
      },
      {
        name: `Inactive Employees: ${employeeGlance?.inactive ?? 0}`,
        value: employeeGlance?.inactive ?? 0,
        color: '#5577FF',
      },
    ]);
  }, [employeeGlance]);

  const getRandomColor = () => {
    const red = Math.floor(Math.random() * 128);
    const green = Math.floor(Math.random() * 256);
    const blue = Math.floor(Math.random() * 128);
    // eslint-disable-next-line no-bitwise
    const color = (red << 16) | (green << 8) | blue;
    return `#${color.toString(16).padStart(6, '0')}`;
  };

  useEffect(() => {
    const chartData: IChartData[] = [];
    employeesByDepartment?.forEach(item => {
      chartData.push({
        name: `${item.department === '' ? 'Department not provided' : item.department}: ${item.total ?? 0}`,
        value: item.total ?? 0,
        color: getRandomColor(),
      });
    });
    setDepartmentChartData(chartData);
  }, [employeesByDepartment]);

  return (
    <>
      <div
        style={{
          display: 'flex',
          gap: '15px',
          width: '100%',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ marginBottom: '10px' }}>
          <PageTitle>{t('employeeManagement.title')}</PageTitle>
        </div>
      </div>
      <GridContainer>
        <MessagesBulletinsContainer>
          <CardItemContainer>
            <HeaderText>{t('dashboard.activeEmployees.header')}</HeaderText>
            <ActiveInactiveEmployeesChart
              data={glanceChartData ?? []}
              isLoading={isLoadingChartEmployeeGlance || isFetchingChartEmployeeGlance}
            />
          </CardItemContainer>
        </MessagesBulletinsContainer>
        <ProfileContainer>
          <CardItemContainer>
            <HeaderText>{t('dashboard.employees.byDepartment')}</HeaderText>
            <EmployeesByDepartmentChart
              data={departmentChartData ?? []}
              isLoading={isLoadingChartByDepartment || isFetchingChartByDepartment}
            />
          </CardItemContainer>
          <CardItemContainer style={{ display: 'none' }}>
            <HeaderText>{t('employeeManagement.profile.title')}</HeaderText>

            <div style={{ display: 'flex', gap: '15px' }}>
              <ProfileSmallCard>
                <ProfileSmallCardTitle>{t('employeeManagement.profile.pendingChanges')}</ProfileSmallCardTitle>
                <ProfileSmallCardValue>...</ProfileSmallCardValue>
              </ProfileSmallCard>
              <ProfileSmallCard>
                <ProfileSmallCardTitle>{t('employeeManagement.profile.allChanges')}</ProfileSmallCardTitle>
                <ProfileSmallCardValue>...</ProfileSmallCardValue>
              </ProfileSmallCard>
            </div>
          </CardItemContainer>
        </ProfileContainer>
        <EmployeeContainer>
          <CardItemContainer>
            <CardHeaderWithButton>
              <HeaderText>{t('employeeList.pageTitle')}</HeaderText>
              <ActionsContainer>
                <SearchInput
                  value={searchTerm}
                  placeholder="Search..."
                  handleChange={event => {
                    let newValue = event.target.value;
                    newValue = newValue.replace(/[',.']/g, '');
                    setSearchTerm(newValue);
                  }}
                />
                <NavTextLink to="/employee-list" style={{ textDecoration: 'none' }}>
                  <RegularButton>{t('global.button.viewAll')}</RegularButton>
                </NavTextLink>
              </ActionsContainer>
            </CardHeaderWithButton>
            {companyId && (
              <EmployeeListTable
                searchTerm={debouncedSearchTerm}
                companyId={companyId}
                columns={employeeListInitialColumns}
                employeeStatus="1"
              />
            )}
          </CardItemContainer>
        </EmployeeContainer>
      </GridContainer>
    </>
  );
};
export default EmployeeMangement;
